import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';

/**
 * Three-way delete for a hierarchy parent that has items:
 *  - Delete it and its items (cascade soft-delete)
 *  - Move the items to other parent(s) first, then delete the emptied parent
 *  - Cancel
 *
 * Props:
 *  - entityName    : label of the thing being deleted (e.g. program name)
 *  - parentNoun    : "program" | "component"
 *  - childNoun     : "components" | "goals"
 *  - items      : [{ id, name }]
 *  - targets       : [{ value, label }]  other parents to move into (exclude self + deleted)
 *  - childIdKey    : payload key for the child id     (e.g. "component_id")
 *  - targetIdKey   : payload key for the target id    (e.g. "target_program_id")
 *  - onCascade()   : delete everything (omit + allowCascade=false to hide this option
 *                    when cascading is too destructive, e.g. deleting a client's programs)
 *  - onMove(moves) : moves = [{ [childIdKey]: id, [targetIdKey]: targetId }]
 *  - onClose()
 *  - busy
 *  - allowCascade  : show the "delete it and its {childNoun}" option (default true)
 */
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 };
const card = { background: 'white', borderRadius: '10px', padding: '22px 24px', width: '560px', maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' };
const btn = (bg, extra) => ({ padding: '9px 16px', background: bg, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', ...extra });
const ghost = { padding: '9px 16px', background: '#ecf0f1', color: '#2c3e50', border: '1px solid #d0d7de', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' };

export default function ReparentDeleteModal({
  entityName, parentNoun, childNoun, items, targets,
  childIdKey, targetIdKey, onCascade, onMove, onClose, busy,
  allowCascade = true,
}) {
  const [mode, setMode] = useState('choose');  // 'choose' | 'move'
  const [moveAll, setMoveAll] = useState('');
  const [perChild, setPerChild] = useState({});

  const targetFor = (cid) => perChild[cid] || moveAll;
  const allAssigned = items.every(c => targetFor(c.id));

  const doMove = () => onMove(items.map(c => ({
    [childIdKey]: c.id, [targetIdKey]: Number(targetFor(c.id)),
  })));

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        <h3 style={{ margin: '0 0 6px', color: '#2c3e50' }}>Delete "{entityName}"?</h3>
        <p style={{ color: '#555', fontSize: '13px', marginTop: 0 }}>
          This {parentNoun} has <strong>{items.length} {childNoun}</strong>.
        </p>

        {mode === 'choose' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '14px' }}>
            {allowCascade && (
              <button style={btn('#c0392b')} disabled={busy} onClick={onCascade}>
                Delete it and its {childNoun}
              </button>
            )}
            <button style={btn('#2980b9')} disabled={busy} onClick={() => setMode('move')}>
              Move {childNoun} to another {parentNoun} first…
            </button>
            <button style={ghost} disabled={busy} onClick={onClose}>Cancel</button>
          </div>
        ) : (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>Move all to</div>
            <SearchableSelect options={targets} value={moveAll} onChange={setMoveAll} placeholder="Pick a target…" />

            <div style={{ fontSize: '12px', color: '#777', margin: '14px 0 6px' }}>
              Per-{childNoun.replace(/s$/, '')} override (optional):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {items.map(c => (
                  <tr key={c.id}>
                    <td style={{ padding: '5px 8px 5px 0', fontSize: '13px', color: '#2c3e50', whiteSpace: 'nowrap' }}>{c.name}</td>
                    <td style={{ padding: '4px 0' }}>
                      <SearchableSelect
                        options={targets}
                        value={perChild[c.id] || ''}
                        onChange={(v) => setPerChild(p => ({ ...p, [c.id]: v }))}
                        placeholder={moveAll ? '(using “move all”)' : 'Pick a target…'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!allAssigned && (
              <div style={{ color: '#c0392b', fontSize: '12px', marginTop: '8px' }}>
                Pick a “move all” target or set one for every {childNoun.replace(/s$/, '')}.
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button style={btn('#c0392b')} disabled={!allAssigned || busy} onClick={doMove}>
                Move &amp; delete {parentNoun}
              </button>
              <button style={ghost} disabled={busy} onClick={() => setMode('choose')}>Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
